import { Injectable, inject, signal, effect } from '@angular/core';
import { Environment } from '@micro-core/environment';
import { UserAttributesInterface } from '../Auth/bs-auth.service';
import { Intercom } from '@supy-io/ngx-intercom';
import { BehaviorSubject, filter, finalize, Observable, retry, tap, throwError, timer } from 'rxjs';
import { FeatureFlagService } from '../FeatureFlag/feature-flag.service';
import { FirebaseService, KnownFeatureFlags } from '@brightside-web/desktop/data-access/shared';
import { Router } from '@angular/router';
import { catchError, take } from 'rxjs/operators';
import { BsApiService, GenericResponseCode } from '../API/bs-api.service';
import { SimpleGlobalModalService } from '../GlobalModal/simple-global-modal.service';

interface ChatTokenPayload {
  payload: {token: string};
}

@Injectable({
  providedIn: 'root',
})
export class BsChatService {
  chatToken = signal<string | null>(null);
  protected _bsApi = inject(BsApiService);

  protected _globalModalSvc: SimpleGlobalModalService = inject(SimpleGlobalModalService);
  private _chatLoaded = new BehaviorSubject<boolean>(false);
  public readonly chatLoaded: Observable<boolean> = this._chatLoaded.asObservable();
  private _messengerReady = new BehaviorSubject<boolean>(false);
  private _isFetchingToken = false;
  private _inMaintenance = signal(false) ;
  private _chatLoadingInProgress = false;

  private _rules: { key: string; state: string }[];

  private _pendingIntercomData = signal<any | null>(null);
  private _flagRequiresToken: boolean;

  constructor(
    private env: Environment,
    private intercom: Intercom,
    private featureFlagSvc: FeatureFlagService,
    private analytics: FirebaseService,
    // Microapps don't like the MaintenanceService being imported into this service
    // private _maintenanceService: MaintenanceService,
    private _router: Router
  ) {
    this.analytics.ensureInitialized().then(() => {
      this.getRules();
    });

    effect(() => {
      const token = this.chatToken();
      const data = this._pendingIntercomData();

      if (this._flagRequiresToken && token && data) {
        this.intercom.boot({ ...data, intercom_user_jwt: token });
        this._listenForMessengerReady();
      } else if (data && !this._flagRequiresToken) {
        this.intercom.boot(data);
        this._listenForMessengerReady();
      }
    }, { allowSignalWrites: true });

    effect(() => {
      const maintenance = this._inMaintenance();
      if (maintenance) {
        this.clearSession();
      }
    });
  }

  getJwtToken() {
    if (this._isFetchingToken) {
      return;
    }
    this._isFetchingToken = true;

    this._bsApi
      .get<ChatTokenPayload>('api-mobile', '/chat/token')
      .pipe(
        tap((response) => {
          if (response.result.code === GenericResponseCode.SERVICE_OK) {
            this.chatToken.set(response.payload.token);
          }
        }),
        retry({
          count: 3,
          delay: () => timer(1000),
        }),
        catchError((err) => {
          this._globalModalSvc.createGenericErrorModal('chat_token', () => {
            this.getJwtToken();
            this._globalModalSvc.clearModal();
          });
          return throwError(() => err);
        }),
        finalize(() => {
          this._isFetchingToken = false; // allow next attempt
        })
      )
      .subscribe();
  }

  /** @param openMessages Opens the Messages tab instead of letting Intercom pick the view. */
  public showChat(openMessages = false): void {
    // Checked first because Intercom never boots during maintenance.
    if (this._inMaintenance()) {
      this._router.navigate(['maintenance', 'feature']);
    } else if (this._messengerReady.value) {
      if (openMessages) {
        this.intercom.showMessages();
      } else {
        this.intercom.show();
      }
    } else if (!this._chatLoadingInProgress) {
      this._chatLoadingInProgress = true;

      this._messengerReady
        .pipe(
          filter((isReady) => isReady),
          take(1)
        )
        .subscribe({
          next: () => {
            this._chatLoadingInProgress = false;
            this.showChat(openMessages);
          },
          error: () => {
            this._chatLoadingInProgress = false;
          },
        });
    }
  }

  clearSession() {
    this._messengerReady.next(false);
    this.intercom.shutdown();
  }

  // Calls made before the Messenger boots are dropped. ngx-intercom does not wrap `ready`, so call window.Intercom.
  private _listenForMessengerReady() {
    (window as any).Intercom?.('ready', () => this._messengerReady.next(true));
  }

  setChatLoaded(b: boolean) {
    this._chatLoaded.next(b);
  }

  intercomBoot(attributes: UserAttributesInterface) {
    if (!this._inMaintenance()) {
      this.featureFlagSvc.getFlag(KnownFeatureFlags.ENABLECHATTOKENINAPPS).subscribe((flag) => {
        if (flag) {
          this.getJwtToken();
          const intercomUserId = attributes['intercom_external_id']
            ? attributes['intercom_external_id']
            : attributes.phone_number;
          this._pendingIntercomData.set({ user_id: intercomUserId, language: attributes['locale'] });
          this._flagRequiresToken = true;
        } else {
          this._flagRequiresToken = false;
          this.prepareIntercomData(attributes);
        }
      });
    }
  }

  prepareIntercomData(attributes: UserAttributesInterface) {
    const intercomUserId = attributes['intercom_external_id'] ? attributes['intercom_external_id'] : attributes.phone_number;
    const regex = /%1\$s/gi;
    const data: any = {
      app_id: this.env.intercomAppId,
      language: attributes['locale'],
      // Supports all optional configuration.
      widget: {
        activator: '#intercom',
      },
      name: attributes['first_name'] + ' ' + attributes['last_name'],
      phone: attributes['phone_number'],
      email: attributes['email'],
      user_id: intercomUserId,
      guid: attributes['guid'],
      custom_attributes: {
        company: attributes['company'],
      },
      'crm link': this.env.crmUrl.replace(regex, attributes['sf_contact_id'] as string),
      hide_default_launcher: false,
    };

    this._pendingIntercomData.set(data);
  }

  // Microapps don't like the MaintenanceService being imported into this service
  private checkMaintenanceForChat() {
    this._rules.forEach((rule) => {
      const { key, state } = rule;
      // Set to true if any rule for the product has inMaintenance: true
      if (( key==='chat' || key === 'chat_action' ) && state === 'MAINTENANCE') {
        this._inMaintenance.set(true);
      }
    });
  }

  private getRules() {
    try {
      this._rules = JSON.parse(this.analytics.getValueAsString('serviceInventoryOverride'));
      this.checkMaintenanceForChat();
    } catch (e) {
      setTimeout(() => {
        // keep trying until we get the rules
        this.getRules();
      }, 150);
    }
  }
}
